import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useAuthenticated} from "nate-react-api-helpers";
import {api} from "../../api/API";
import {useSocketNotifications} from "../WebSocketProvider";
import {useSnackbar} from "../Snackbar";

export type UserPrefMap = {[key: string]: string};

type UserPrefContextValue = {
    prefs: UserPrefMap;
    set(key: string, value: string): void;
    clear(key: string): void;
    reload(): void;
}

export const UserPreferencesContext = createContext<UserPrefContextValue>({
    prefs: {},
    set: () => {
        throw new Error("set: invalid context")
    },
    clear: () => {
        throw new Error("clear: invalid context")
    },
    reload: () => {
        throw new Error("reload: invalid context")
    },
});

export function useUserPreferences() {
    return useContext(UserPreferencesContext);
}

export const userPrefChangedSocketKey = "user-preference:changed";

// Loads the current user's preferences once per login and keeps them in memory
// across navigations. Re-fetches when a preference changes (this tab or another).
// Children wait for the first load so hooks see the final value on mount.
export function UserPreferencesProvider(props: PropsWithChildren<{}>) {
    const auth = useAuthenticated();
    const snack = useSnackbar();
    const [prefs, setPrefs] = useState<UserPrefMap | null>(null);
    const loadCount = useRef(0);

    const reload = useCallback(async () => {
        const n = ++loadCount.current;

        try {
            const list = await api.userPreferences.list({});
            if (n !== loadCount.current) return;

            const next: UserPrefMap = {};
            list.forEach(p => next[p.key] = p.value);
            setPrefs(next);
        } catch (e: any) {
            if (n !== loadCount.current) return;
            console.error(e);
            setPrefs({});
        }
    }, []);

    useEffect(() => {
        if (!auth.authenticated) {
            loadCount.current++;
            setPrefs(null);
            return;
        }

        reload();
    }, [auth.authenticated, reload]);

    useSocketNotifications(userPrefChangedSocketKey, () => {
        reload();
    }, [reload]);

    const upsert = useCallback(async (key: string, value: string, archived: boolean) => {
        setPrefs(old => {
            const next = {...(old || {})};
            if (archived) {
                delete next[key];
            } else {
                next[key] = value;
            }

            return next;
        });

        try {
            await api.userPreferences.upsert({key, value, archived});
        } catch (e: any) {
            snack.error("Failed to save preference: " + e.toString());
            reload();
        }
    }, [reload, snack]);

    const ctx = useMemo<UserPrefContextValue>(() => ({
        prefs: prefs || {},
        set: (key, value) => upsert(key, value, false),
        clear: (key) => upsert(key, "", true),
        reload,
    }), [prefs, upsert, reload]);

    if (auth.authenticated && prefs === null) return null;

    return (
        <UserPreferencesContext.Provider value={ctx}>
            {props.children}
        </UserPreferencesContext.Provider>
    );
}
